/**
 * The Surveys menu of the Admin dashboard: everything staff use to run a
 * survey, in the order the work happens. Find it, field it, keep its data
 * clean, size the next one, and the reference behind all of it.
 *
 * The Surveys menu's own list, read into the Admin dashboard's menus (admin-menu.ts):
 * the label, the line under it and the icon are written once, here. Each item
 * names the gate its page applies, so the menu never offers a page that would
 * refuse the person. Pure.
 */
import { STAFF_TOOLS, type StaffToolKey } from './tools';

export type SurveyMenuGate =
    /** One of the staff tools that replace SOTG's control panel: internal plus its flag. */
    | { staffTool: StaffToolKey }
    /** Internal staff plus a feature flag. */
    | { flag: string }
    /** Any internal staff member. */
    | 'internal';

export interface SurveyMenuItem {
    key: string;
    label: string;
    /** One line: what the page is for, in the words of the person using it. */
    description: string;
    /** A Lucide icon name (Iconify `lucide:`). */
    icon: string;
    href: string;
    gate: SurveyMenuGate;
}

export interface SurveyMenuSection {
    key: string;
    title: string;
    /** What the section holds, for the Surveys overview. */
    blurb: string;
    items: SurveyMenuItem[];
}

const hrefOf = (key: StaffToolKey): string => {
    const tool = STAFF_TOOLS.find((t) => t.key === key) as { href?: string } | undefined;
    if (!tool?.href) throw new Error(`Staff tool ${key} has no page.`);
    return tool.href;
};

export const SURVEY_MENU: readonly SurveyMenuSection[] = [
    {
        key: 'find',
        title: 'Find',
        blurb: 'Look up a survey, its project or a panelist.',
        items: [
            {
                key: 'search',
                label: 'Search',
                description: 'Any survey, project, client or panelist, by name, id, email or phone',
                icon: 'lucide:search',
                href: hrefOf('search'),
                gate: { staffTool: 'search' },
            },
            {
                key: 'projects',
                label: 'Projects',
                description: "A project's surveys, their status and their exports",
                icon: 'lucide:folder-kanban',
                href: hrefOf('projects'),
                gate: { staffTool: 'projects' },
            },
            {
                key: 'panelists',
                label: 'Panelists',
                description: "A panelist's profile, devices, surveys taken and payments",
                icon: 'lucide:user-round-search',
                href: hrefOf('panelists'),
                gate: { staffTool: 'panelists' },
            },
        ],
    },
    {
        key: 'field',
        title: 'Field',
        blurb: 'Who a survey goes to, and keeping it running.',
        items: [
            {
                key: 'fielding-console',
                label: 'Fielding',
                description: 'Every survey in field now: pause, resume or close it',
                icon: 'lucide:radio-tower',
                href: hrefOf('fielding-console'),
                gate: { staffTool: 'fielding-console' },
            },
            {
                key: 'release-groups',
                label: 'Release groups',
                description: 'Lists of panelists a survey is sent to',
                icon: 'lucide:users-round',
                href: hrefOf('release-groups'),
                gate: { staffTool: 'release-groups' },
            },
            {
                key: 'location-groups',
                label: 'Location groups',
                description: 'The stores a store survey fields at',
                icon: 'lucide:map-pin',
                href: hrefOf('location-groups'),
                gate: { staffTool: 'location-groups' },
            },
        ],
    },
    {
        key: 'data',
        title: 'Data quality',
        blurb: 'Who counts, what they answered, and how well open ends are coded.',
        items: [
            {
                key: 'scrubber',
                label: 'Data scrubber',
                description: 'Review respondents, then reject, retire or pay them',
                icon: 'lucide:shield-check',
                href: hrefOf('scrubber'),
                gate: { staffTool: 'scrubber' },
            },
            {
                key: 'data-corrections',
                label: 'Answer corrections',
                description: 'Correct recorded answers in bulk from a spreadsheet',
                icon: 'lucide:file-pen-line',
                href: hrefOf('data-corrections'),
                gate: { staffTool: 'data-corrections' },
            },
            {
                key: 'oe-coding-benchmark',
                label: 'Open-end coding accuracy',
                description: "How often Autopilot's open-end coding matches the published coding",
                icon: 'lucide:gauge',
                href: '/tools/oe-coding-benchmark',
                gate: 'internal',
            },
        ],
    },
    {
        key: 'feasibility',
        title: 'Feasibility',
        blurb: 'The rates behind every feasibility answer, and how well DANI asks.',
        items: [
            {
                key: 'feasibility-rates',
                label: 'Feasibility rates',
                description:
                    'The funnel rates each check uses, where they come from, and overrides',
                icon: 'lucide:sliders-horizontal',
                href: '/tools/feasibility-rates',
                gate: { flag: 'feasibility-dani' },
            },
            {
                key: 'feasibility-report-types',
                label: 'Report plans',
                description: 'The plan file behind each feasibility report',
                icon: 'lucide:file-text',
                href: '/tools/feasibility-report-types',
                gate: { flag: 'feasibility-dani' },
            },
            {
                key: 'feasibility-benchmark',
                label: 'DANI request benchmark',
                description: "DANI's feasibility requests, scored against an analyst's",
                icon: 'lucide:target',
                href: '/tools/feasibility-benchmark',
                gate: { flag: 'feasibility-dani' },
            },
            {
                key: 'legacy-feasibility',
                label: 'Legacy feasibility',
                description: 'The earlier feasibility tool, until the new checks replace it',
                icon: 'lucide:history',
                href: '/feasibility',
                gate: { flag: 'studio.nav.feasibility' },
            },
        ],
    },
    {
        key: 'reference',
        title: 'Reference',
        blurb: 'The lists every survey tool relies on.',
        items: [
            {
                key: 'reference-data',
                label: 'Reference data',
                description: 'Recruiters, survey groups and the other lists surveys use',
                icon: 'lucide:list-tree',
                href: hrefOf('reference-data'),
                gate: { staffTool: 'reference-data' },
            },
        ],
    },
];

export function canOpen(
    gate: SurveyMenuGate,
    access: { isInternal: boolean; hasFlag: (key: string) => boolean },
): boolean {
    if (!access.isInternal) return false;
    if (gate === 'internal') return true;
    if ('flag' in gate) return access.hasFlag(gate.flag);
    const tool = STAFF_TOOLS.find((t) => t.key === gate.staffTool);
    return !!tool && access.hasFlag(tool.flag);
}

/** The menu as this person may use it: a section with nothing they may open is left out. */
export function surveyMenuFor(access: {
    isInternal: boolean;
    hasFlag: (key: string) => boolean;
}): SurveyMenuSection[] {
    return SURVEY_MENU.map((s) => ({
        ...s,
        items: s.items.filter((i) => canOpen(i.gate, access)),
    })).filter((s) => s.items.length > 0);
}
